/**
 * decision-labels.ts (#571)
 *
 * UI 側で decision_result（enum value）を人間が読めるラベルに解決する fallback。
 * 2 段階フォールバック:
 *   1. decision_options に value 一致する option があればその label を使う
 *   2. 無ければ canonical map で enum → ラベル変換（"attend_in_person" → "現地参加" 等）
 *   3. 全て失敗したら raw value を表示（既存挙動）
 *
 * canonical map は `src/lib/judgment-decision-normalizer.ts` の `formatDecisionValueForDisplay` と同じ。
 * tsconfig の rootDir 制約で shared import できないため map を複製している。
 * `tests/unit/decision-labels-drift.test.ts` の drift test で 3 箇所の整合を assert する。
 *
 * 同期対象:
 *   - src/lib/judgment-decision-normalizer.ts (canonical)
 *   - public/ts/decision-labels.ts (this file)
 *   - chrome-extension/src/js/components/decision-section.ts
 */

export const DECISION_LABEL_JA: Record<string, string> = {
  attend: "参加する",
  attend_in_person: "現地参加",
  attend_online: "オンライン参加",
  attend_all_days: "全日参加",
  attend_day_1: "1日目に参加",
  attend_day_2: "2日目に参加",
  attend_all_sessions: "全セッション参加",
  attend_session_1: "第1部に参加",
  attend_session_2: "第2部に参加",
  attend_networking: "交流会に参加",
  decline: "不参加",
  defer: "保留",
  confirm_current_attendees: "この参加者で招待する",
  request_more_attendees: "参加者を追加確認する",
  approve: "承認する",
  reject: "見送る",
  propose_alternative_dates: "別の候補日を提案する",
};

export const DECISION_LABEL_EN: Record<string, string> = {
  attend: "Attend",
  attend_in_person: "Attend in person",
  attend_online: "Attend online",
  attend_all_days: "Attend all days",
  attend_day_1: "Attend day 1",
  attend_day_2: "Attend day 2",
  attend_all_sessions: "Attend all sessions",
  attend_session_1: "Attend session 1",
  attend_session_2: "Attend session 2",
  attend_networking: "Attend networking",
  decline: "Decline",
  defer: "Defer",
  confirm_current_attendees: "Invite current attendees",
  request_more_attendees: "Confirm more attendees",
  approve: "Approve",
  reject: "Reject",
  propose_alternative_dates: "Propose alternative dates",
};

type DecisionOptionLike = { label?: string | null; value?: string | null };
type SubtaskLike = {
  decision_result?: string | null;
  decision_options?: DecisionOptionLike[] | null;
};

const DATE_SELECTION_PREFIX = "select_date:";

export function decodeDateSelectionValue(value: string): string | null {
  if (!value.startsWith(DATE_SELECTION_PREFIX)) return null;
  try {
    return decodeURIComponent(value.slice(DATE_SELECTION_PREFIX.length));
  } catch {
    return value.slice(DATE_SELECTION_PREFIX.length);
  }
}

export function resolveDecisionResultLabel(
  st: SubtaskLike,
  lang: "ja" | "en" = "ja",
): string {
  const result = st.decision_result;
  if (!result) return "";
  const matched = (st.decision_options ?? []).find(
    (opt) => opt?.value === result || opt?.label === result,
  );
  if (matched?.label) return matched.label;
  const decodedDate = decodeDateSelectionValue(result);
  if (decodedDate) return decodedDate;
  const map = lang === "en" ? DECISION_LABEL_EN : DECISION_LABEL_JA;
  return map[result] ?? result;
}
